import { organisationIndexQueryKey, useOrganisationAcceptInvitation, useOrganisationIndex } from '@jobilla/nexus-sdk'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthErrorAlert, JobillaBrandLogo } from '../../ui'

export interface OrganizationListProps {
	onSelect: (org: { id: string; slug: string; workosId: string | null }) => void
	onCreate?: () => void
	/** When provided, uses these orgs instead of fetching from the API. Useful during auth flows where the user isn't authenticated yet. */
	organizations?: Array<{ id: string; name: string }>
}

export function OrganizationList({ onSelect, onCreate, organizations: externalOrgs }: OrganizationListProps) {
	const { t } = useTranslation('auth')
	const queryClient = useQueryClient()
	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300)
		return () => clearTimeout(timer)
	}, [search])

	const organisationsIndexQuery = useOrganisationIndex(
		debouncedSearch ? { 'filter[name]': debouncedSearch } : undefined,
		{ enabled: !externalOrgs, placeholderData: keepPreviousData },
	)
	const organisations = externalOrgs
		? externalOrgs.map((org) => ({
			id: org.id,
			name: org.name,
			slug: '',
			workosId: org.id,
			membershipStatus: 'active',
			membershipId: null,
			meta: null,
			updatedAt: '',
		}))
		: organisationsIndexQuery.data
	const isLoading = externalOrgs ? false : organisationsIndexQuery.isLoading
	const isPlaceholderData = externalOrgs ? false : organisationsIndexQuery.isPlaceholderData
	const error = externalOrgs ? null : organisationsIndexQuery.error
	const [acceptingId, setAcceptingId] = useState<string | null>(null)

	const acceptMutation = useOrganisationAcceptInvitation({
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() })
		},
	})

	const handleAccept = (invitationId: string) => {
		setAcceptingId(invitationId)
		acceptMutation.mutate({ invitationId }, {
			onSettled: () => setAcceptingId(null),
		})
	}

	return (
		<div className="w-full max-w-lg mx-auto p-8">
			<JobillaBrandLogo />

			<h1 className="mt-8 text-3xl font-semibold text-gray-800 tracking-[0.01em]">
				{t('selectOrganization.title', 'Select organization')}
			</h1>
			<p className="mt-3 text-base text-gray-500">
				{t('selectOrganization.description', 'Choose an organization to continue')}
			</p>

			{!externalOrgs && (
				<div className="mt-6">
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t('selectOrganization.searchPlaceholder', 'Search organizations...')}
						className="
							w-full px-4 py-3 rounded-xl border border-gray-200
							text-gray-800 placeholder-gray-400
							focus:outline-none focus:ring-2 focus:ring-[#225DFC]/50 focus:border-[#225DFC]/40
							transition-all duration-200
						"
					/>
				</div>
			)}

			<div
				className={`mt-8 space-y-3 transition-opacity duration-150 ${
					isPlaceholderData ? 'opacity-50' : 'opacity-100'
				}`}
			>
				{error ? <AuthErrorAlert message={error.message ?? 'Failed to load organizations'} /> : null}

				{isLoading
					? (
						<div className="space-y-3">
							{[1, 2, 3].map((i) => (
								<div
									key={i}
									className="h-16 rounded-xl bg-gray-100 animate-pulse"
								/>
							))}
						</div>
					)
					: null}

				{organisations?.map((org) => {
					const isPending = org.membershipStatus === 'pending'
					const invitationId = org.membershipId
					const isAccepting = acceptingId === invitationId

					if (isPending && invitationId) {
						return (
							<div
								key={org.id}
								className="
									w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200
									border-dashed
								"
							>
								<div className=" w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold text-sm shrink-0">
									{org.name.charAt(0).toUpperCase()}
								</div>
								<div className="min-w-0 flex-1">
									<div className="font-medium text-gray-800 truncate">{org.name}</div>
									<div className="text-sm text-gray-400 truncate">
										{t('selectOrganization.pendingInvite', 'Pending invitation')}
									</div>
								</div>
								<button
									type="button"
									disabled={isAccepting}
									onClick={() => handleAccept(invitationId)}
									className="
										shrink-0 px-4 py-2 rounded-full text-sm font-medium
										bg-[#225DFC] text-white
										hover:bg-[#1a4cd4] transition-colors
										disabled:opacity-50 disabled:cursor-not-allowed
									"
								>
									{isAccepting
										? t('selectOrganization.joining', 'Joining...')
										: t('selectOrganization.join', 'Join')}
								</button>
							</div>
						)
					}

					return (
						<button
							key={org.id}
							type="button"
							onClick={() => onSelect({ id: org.id, slug: org.slug, workosId: org.workosId })}
							className="
								w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200
								hover:border-[#225DFC]/40 hover:bg-[#225DFC]/5
								transition-all duration-200 cursor-pointer text-left
								focus:outline-none focus:ring-2 focus:ring-[#225DFC]/50 focus:ring-offset-2
							"
						>
							<div className="
								w-10 h-10 rounded-full bg-gradient-to-r from-[#225DFC] to-[#9726FA]
								flex items-center justify-center text-white font-semibold
								text-sm shrink-0
						">
								{org.name.charAt(0).toUpperCase()}
							</div>
							<div className="min-w-0">
								<div className="font-medium text-gray-800 truncate">{org.name}</div>
								<div className="text-sm text-gray-400 truncate">{org.slug}</div>
							</div>
						</button>
					)
				})}

				{!isLoading && !isPlaceholderData && organisations?.length === 0 && (
					<p className="text-center text-gray-400 py-4">
						{debouncedSearch
							? t('selectOrganization.noSearchResults', 'No organizations found matching "{{query}}"', {
								query: debouncedSearch,
							})
							: t('selectOrganization.noOrganizations', 'You are not part of any organization yet')}
					</p>
				)}
			</div>

			{onCreate
				? (
					<div className="mt-6">
						<button
							type="button"
							onClick={onCreate}
							className="
							w-full py-3 px-4 rounded-full font-medium text-base
							border-2 border-dashed border-gray-300
							text-gray-500 hover:text-[#225DFC] hover:border-[#225DFC]/40
							transition-all duration-200 cursor-pointer
							focus:outline-none focus:ring-2 focus:ring-[#225DFC]/50 focus:ring-offset-2
						"
						>
							{t('selectOrganization.createNew', '+ Create new organization')}
						</button>
					</div>
				)
				: null}
		</div>
	)
}
